/**
 * FindAccountPage Component
 * 아이디/비밀번호 변경 페이지
 * 이름과 이메일을 입력하여 아이디 찾기 또는 비밀번호 변경 수행
 * VROONG 브랜드 통일 디자인
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFindAccount } from '../hooks/useFindAccount';
import styles from '../styles/RegistrationPage.module.css';

const FindAccountPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const type = (searchParams.get('type') || 'id') as 'id' | 'password';

    const [name, setName] = useState('');
    const [userId, setUserId] = useState('');
    const [email, setEmail] = useState('');
    const [foundUserId, setFoundUserId] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const { handleFindId, handleFindPassword, isLoading, error } = useFindAccount();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFoundUserId(null);
        setSuccessMessage(null);

        if (type === 'id') {
            const result = await handleFindId(name, email);
            if (result.success && result.userId) {
                setFoundUserId(result.userId);
                setSuccessMessage(result.message || '아이디를 찾았습니다.');
            }
        } else {
            const result = await handleFindPassword(userId, email);
            if (result.success && !result.exists) {
                setSuccessMessage(result.message || '비밀번호 변경이 완료되었습니다.');
            }
        }
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    const handleGoToLogin = () => {
        navigate('/login');
    };

    const handleReset = () => {
        setFoundUserId(null);
        setSuccessMessage(null);
        setName('');
        setUserId('');
        setEmail('');
    };

    // 아이디 찾기 성공
    if (foundUserId) {
        return (
            <div className={styles['registration-screen']}>
                <div className={styles['registration-header']}>
                    <div className={styles['header-content']}>
                        <h1 className={styles['header-title']}>아이디 찾기</h1>
                    </div>
                </div>
                <div className={styles['registration-container']}>
                    <div className={styles['card']}>
                        <div className={styles['status-icon-wrapper']}>
                            <i className="fas fa-check" style={{ color: 'white', fontSize: '32px' }}></i>
                        </div>
                        <h2 className={styles['status-title']}>아이디를 찾았습니다</h2>
                        <p className={styles['status-description']}>
                            회원님의 아이디는 다음과 같습니다.
                        </p>
                        <div style={{
                            padding: '16px',
                            backgroundColor: 'rgba(5, 205, 130, 0.1)',
                            borderRadius: '12px',
                            border: '2px solid rgba(5, 205, 130, 0.3)',
                            marginBottom: '20px',
                            textAlign: 'center'
                        }}>
                            <span style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                color: '#05CD82',
                                letterSpacing: '2px'
                            }}>
                                {foundUserId}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles['btn-primary']}
                            onClick={handleGoToLogin}
                        >
                            로그인하기
                        </button>
                        <button
                            type="button"
                            className={styles['btn-secondary']}
                            onClick={() => navigate('/find-account?type=password')}
                        >
                            비밀번호 변경하기
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // 비밀번호 변경 성공
    if (successMessage) {
        return (
            <div className={styles['registration-screen']}>
                <div className={styles['registration-header']}>
                    <div className={styles['header-content']}>
                        <h1 className={styles['header-title']}>비밀번호 변경</h1>
                    </div>
                </div>
                <div className={styles['registration-container']}>
                    <div className={styles['card']}>
                        <div className={styles['status-icon-wrapper']}>
                            <i className="fas fa-check" style={{ color: 'white', fontSize: '32px' }}></i>
                        </div>
                        <h2 className={styles['status-title']}>비밀번호 변경 완료</h2>
                        <p className={styles['status-description']}>
                            {successMessage}
                        </p>
                        <button
                            type="button"
                            className={styles['btn-primary']}
                            onClick={handleGoToLogin}
                        >
                            로그인하기
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles['registration-screen']}>
            {/* 헤더 */}
            <div className={styles['registration-header']}>
                <div className={styles['header-content']}>
                    <button className={styles['back-btn']} onClick={handleBackToLogin}>
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h1 className={styles['header-title']}>
                        {type === 'id' ? '아이디 찾기' : '비밀번호 변경'}
                    </h1>
                    <div className={styles['header-spacer']}></div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['registration-container']}>
                <div className={styles['card']}>
                    <div className={styles['card-header']}>
                        <div className={styles['card-icon']}>
                            <i className={type === 'id' ? 'fas fa-search' : 'fas fa-key'}></i>
                        </div>
                        <h2 className={styles['card-title']}>
                            {type === 'id' ? '아이디 찾기' : '비밀번호 변경'}
                        </h2>
                        <p className={styles['card-description']}>
                            {type === 'id'
                                ? '이름과 이메일을 입력하여 아이디를 찾아주세요.'
                                : '아이디와 이메일을 입력하여 비밀번호를 재설정해주세요.'}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className={styles['form-section']}>
                        {type === 'id' ? (
                            <div className={styles['form-field']}>
                                <label className={styles['field-label']}>
                                    이름 <span className={styles['required']}>*</span>
                                </label>
                                <div className={styles['input-wrapper']}>
                                    <i className={`fas fa-user ${styles['input-icon']}`}></i>
                                    <input
                                        type="text"
                                        className={styles['input-field']}
                                        placeholder="이름을 입력해주세요"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className={styles['form-field']}>
                                <label className={styles['field-label']}>
                                    아이디 <span className={styles['required']}>*</span>
                                </label>
                                <div className={styles['input-wrapper']}>
                                    <i className={`fas fa-id-card ${styles['input-icon']}`}></i>
                                    <input
                                        type="text"
                                        className={styles['input-field']}
                                        placeholder="아이디를 입력해주세요"
                                        value={userId}
                                        onChange={(e) => setUserId(e.target.value)}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                        )}

                        <div className={styles['form-field']}>
                            <label className={styles['field-label']}>
                                이메일 <span className={styles['required']}>*</span>
                            </label>
                            <div className={styles['input-wrapper']}>
                                <i className={`fas fa-envelope ${styles['input-icon']}`}></i>
                                <input
                                    type="email"
                                    className={styles['input-field']}
                                    placeholder="이메일을 입력해주세요"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        {error && (
                            <div className={styles['error-message']}>
                                <i className="fas fa-exclamation-circle"></i>
                                {error}
                            </div>
                        )}

                        <button type="submit" className={styles['btn-primary']} disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <span className={styles['btn-spinner']}></span>
                                    처리 중...
                                </>
                            ) : type === 'id' ? '아이디 찾기' : '비밀번호 변경'}
                        </button>

                        <button
                            type="button"
                            className={styles['btn-secondary']}
                            onClick={handleBackToLogin}
                        >
                            로그인 페이지로 돌아가기
                        </button>

                        {/* 아이디 찾기 / 비밀번호 변경 전환 링크 */}
                        <div className={styles['login-link']}>
                            {type === 'id' ? (
                                <>
                                    <span>비밀번호를 잊으셨나요?</span>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/find-account?type=password')}
                                        className={styles['text-link']}
                                    >
                                        비밀번호 변경
                                    </button>
                                </>
                            ) : (
                                <>
                                    <span>아이디를 잊으셨나요?</span>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/find-account?type=id')}
                                        className={styles['text-link']}
                                    >
                                        아이디 찾기
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default FindAccountPage;
